/**
 * Loan Car Allocation API Service
 *
 * Endpoints:
 *   GET    /api/bookings/loan-cars/available       — availability search
 *   GET    /api/bookings/:bookingUuid/loan-cars     — allocations on a booking
 *   POST   /api/bookings/:bookingUuid/loan-cars     — add allocation
 *   PUT    /api/bookings/:bookingUuid/loan-cars/:allocationUuid — update
 *   DELETE /api/bookings/:bookingUuid/loan-cars/:allocationUuid — remove
 */

import axiosInstance from './axiosInstance';

/**
 * Find loan cars available for a time window.
 * @param {string} start - ISO local datetime e.g. "2025-03-10T08:00"
 * @param {string} end   - ISO local datetime e.g. "2025-03-12T17:00"
 * @param {string} [excludeAllocation] - UUID of existing allocation to exclude (for edits)
 */
export const getAvailableLoanCarsApi = async (start, end, excludeAllocation = null) => {
    const params = { start, end };
    if (excludeAllocation) params.excludeAllocation = excludeAllocation;
    const response = await axiosInstance.get('/bookings/loan-cars/available', { params });
    return response.data;
};

/**
 * Get all loan car allocations for a booking.
 */
export const getBookingAllocationsApi = async (bookingUuid) => {
    const response = await axiosInstance.get(`/bookings/${bookingUuid}/loan-cars`);
    return response.data;
};

/**
 * Allocate a loan car to a booking.
 * @param {string} bookingUuid
 * @param {{ loanCarUuid, allocationStart, allocationEnd, notes }} data
 */
export const allocateLoanCarApi = async (bookingUuid, data) => {
    const response = await axiosInstance.post(`/bookings/${bookingUuid}/loan-cars`, data);
    return response.data;
};

/**
 * Update an existing allocation window / notes.
 */
export const updateAllocationApi = async (bookingUuid, allocationUuid, data) => {
    const response = await axiosInstance.put(
        `/bookings/${bookingUuid}/loan-cars/${allocationUuid}`,
        data
    );
    return response.data;
};

/**
 * Remove an allocation.
 */
export const removeAllocationApi = async (bookingUuid, allocationUuid) => {
    await axiosInstance.delete(`/bookings/${bookingUuid}/loan-cars/${allocationUuid}`);
};

const loanCarAllocationApi = {
    getAvailableLoanCarsApi,
    getBookingAllocationsApi,
    allocateLoanCarApi,
    updateAllocationApi,
    removeAllocationApi,
};

export default loanCarAllocationApi;